import { apiClient } from './apiClient';
import { debugLogger } from '../utils/debugLogger';

export enum CallType {
  VOICE = 'VOICE',
  VIDEO = 'VIDEO'
}

export enum CallStatus {
  INITIATED = 'INITIATED',
  RINGING = 'RINGING',
  ANSWERED = 'ANSWERED',
  REJECTED = 'REJECTED',
  ENDED = 'ENDED',
  MISSED = 'MISSED',
  FAILED = 'FAILED'
}

export interface VideoCall {
  id: number;
  conversationId: number;
  roomId: string;
  callerId: number;
  type: CallType;
  status: CallStatus;
  startedAt?: string;
  endedAt?: string;
  duration?: number;
  createdAt: string;
  updatedAt: string;
  caller?: {
    id: number;
    username: string;
    displayName: string;
    avatarUrl?: string;
  };
  participants?: CallParticipant[];
}

export interface CallParticipant {
  userId: number;
  username: string;
  displayName: string;
  avatarUrl?: string;
  joinedAt: string;
  leftAt?: string;
  isAudioMuted: boolean;
  isVideoOff: boolean;
}

export interface InitiateCallRequest {
  conversationId: number;
  type: CallType;
}

export interface AnswerCallRequest {
  callId: number;
}

export interface RejectCallRequest {
  callId: number;
  reason?: string;
}

export interface EndCallRequest {
  callId: number;
  roomId: string;
}

export interface CallHistoryItem {
  id: number;
  conversationId: number;
  type: CallType;
  status: CallStatus;
  duration?: number;
  startedAt?: string;
  endedAt?: string;
  caller: {
    id: number;
    displayName: string;
    avatarUrl?: string;
  };
  participantCount: number;
}

interface ApiResponse<T> {
  result: string;
  message: string;
  data: T;
}

class VideoCallService {
  private baseUrl = '/api/calls';

  // Initiate call
  async initiateCall(request: InitiateCallRequest): Promise<VideoCall> {
    const endpoint = `${this.baseUrl}/initiate`;
    debugLogger.logApiCall('POST', endpoint, request);
    try {
      const response = await apiClient.post<ApiResponse<VideoCall>>(endpoint, request);
      debugLogger.logApiResponse('POST', endpoint, response);
      return response.data;
    } catch (error) {
      debugLogger.logApiResponse('POST', endpoint, null, error);
      throw error;
    }
  }

  // Answer call
  async answerCall(request: AnswerCallRequest): Promise<VideoCall> {
    const endpoint = `${this.baseUrl}/answer`;
    debugLogger.logApiCall('POST', endpoint, request);
    try {
      const response = await apiClient.post<ApiResponse<VideoCall>>(endpoint, request);
      debugLogger.logApiResponse('POST', endpoint, response);
      return response.data;
    } catch (error) {
      debugLogger.logApiResponse('POST', endpoint, null, error);
      throw error;
    }
  }

  // Reject call
  async rejectCall(request: RejectCallRequest): Promise<void> {
    const endpoint = `${this.baseUrl}/reject`;
    debugLogger.logApiCall('POST', endpoint, request);
    try {
      await apiClient.post(endpoint, request);
      debugLogger.logApiResponse('POST', endpoint, { success: true });
    } catch (error) {
      debugLogger.logApiResponse('POST', endpoint, null, error);
      throw error;
    }
  }

  // End call
  async endCall(request: EndCallRequest): Promise<VideoCall> {
    const endpoint = `${this.baseUrl}/end`;
    debugLogger.logApiCall('POST', endpoint, request);
    try {
      const response = await apiClient.post<ApiResponse<VideoCall>>(endpoint, request);
      debugLogger.logApiResponse('POST', endpoint, response);
      return response.data;
    } catch (error) {
      debugLogger.logApiResponse('POST', endpoint, null, error);
      throw error;
    }
  }

  // Get call by room ID
  async getCallByRoom(roomId: string): Promise<VideoCall> {
    const endpoint = `${this.baseUrl}/room/${roomId}`;
    debugLogger.logApiCall('GET', endpoint);
    try {
      const response = await apiClient.get<ApiResponse<VideoCall>>(endpoint);
      debugLogger.logApiResponse('GET', endpoint, response);
      return response.data;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }

  // Get call history
  async getCallHistory(page: number = 0, size: number = 20): Promise<CallHistoryItem[]> {
    const endpoint = `${this.baseUrl}/history`;
    debugLogger.logApiCall('GET', endpoint, { page, size });
    try {
      const response = await apiClient.get<ApiResponse<CallHistoryItem[]>>(endpoint, {
        params: { page, size }
      });
      debugLogger.logApiResponse('GET', endpoint, response);
      return response.data;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }
}

export const videoCallService = new VideoCallService();
